import {
  Box,
  Button,
  CloseButton,
  DataList,
  Dialog,
  Flex,
  HStack,
  Icon,
  IconButton,
  Link,
  Portal,
  Separator,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import DOMPurify from 'dompurify';
import { motion } from 'framer-motion';
import parse from 'html-react-parser';
import { t } from 'i18next';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { FaArrowLeft, FaPlus, FaRegLightbulb } from 'react-icons/fa';
import { LuExternalLink } from 'react-icons/lu';
import { PiSealWarningLight } from 'react-icons/pi';

import { type IndicatorSuggestionPublic, LibraryService } from '@/client';
import { DebouncedInput } from '@/components/common/DebouncedInput';
import { CompositeIndicatorInput, IndicatorInput, SimpleIndicatorInput } from '@/components/common/IndicatorInput';
import { SelectCommitment } from '@/components/common/SelectCommitment';
import SelectUnits from '@/components/common/SelectUnits';
import { useIndicatorSuggestionAddForm } from '@/components/library/useIndicatorSuggestionAddForm';

import { useIndicatorAddForm } from './useIndicatorAddForm';

function getSuggestionsQueryOptions({ slug, search }: { slug: string; search: string }) {
  return {
    queryKey: ['suggestions', { slug, search }],
    queryFn: async () =>
      await LibraryService.readLibraryIndicators({
        companySlug: slug,
        limit: 5,
        search: search,
      }),
    enabled: !!search,
  };
}

function getSuggestionQueryOptions({ slug, id }: { slug: string; id: string | undefined }) {
  return {
    queryKey: ['suggestionDetails', { slug, id }],
    queryFn: async () =>
      await LibraryService.readLibraryIndicator({
        companySlug: slug,
        id: id ?? '',
      }),
    enabled: !!id,
  };
}

const IndicatorAddDialog = ({
  children,
  commitmentId,
  order,
  onSuccess,
}: {
  children?: ReactNode;
  commitmentId?: string;
  order?: number;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation();
  const { slug } = useParams({ strict: false });
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState<IndicatorSuggestionPublic | null>(null);

  const { data: suggestionsData } = useQuery(getSuggestionsQueryOptions({ slug, search: searchTerm }));

  const { data: suggestionData } = useQuery(getSuggestionQueryOptions({ slug, id: selectedSuggestion?.id }));

  const form = useIndicatorAddForm({
    slug,
    onSuccess: () => resetModalBehavior(),
  });

  if (commitmentId) {
    form.setFieldValue('commitment_id', commitmentId);
  }
  if (order) {
    form.setFieldValue('order', order);
  }

  const suggestionForm = useIndicatorSuggestionAddForm({
    slug,
    suggestion: suggestionData,
    onSuccess: () => resetModalBehavior(),
  });
  const suggestionFormUnits = useStore(suggestionForm.store, (s) => s.values.units);

  const handleSearchTerm = (value: string) => {
    if (value.trim().split(' ').length >= 1) {
      setSearchTerm(value);
    }
  };

  const resetModalBehavior = () => {
    setSelectedSuggestion(null);
    form.reset();
    suggestionForm.reset();
    setIsOpen(false);
    onSuccess?.();
  };

  useEffect(() => {
    if (suggestionData) {
      suggestionForm.setFieldValue(
        'units',
        suggestionData.formula_units_per_indicator?.map((el) => ({
          unit: '',
          suggestion_id: el.suggestion_id,
        })) ?? []
      );
    } else {
      suggestionForm.reset();
    }
  }, [suggestionData]);

  const category = useStore(form.store, (state) => state.values.category);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        setIsOpen(e.open);
        if (!e.open) setSelectedSuggestion(null);
      }}
      placement="center"
      size="lg"
    >
      <Dialog.Trigger as="div">
        {children ? (
          children
        ) : (
          <Button bg={'primary.500'} size="sm">
            <FaPlus /> {t('indicator.add.btn')}
          </Button>
        )}
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            as="form"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (selectedSuggestion) suggestionForm.handleSubmit();
              else form.handleSubmit();
            }}
          >
            <Dialog.CloseTrigger onClick={() => resetModalBehavior()} />

            <Dialog.Header gap={selectedSuggestion ? '2' : '0'}>
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: selectedSuggestion ? 1 : 0,
                  width: selectedSuggestion ? 'auto' : '0%',
                }}
                transition={{ duration: 0.3 }}
                style={{ alignSelf: 'center' }}
              >
                <Button variant="outline" size="2xs" onClick={() => setSelectedSuggestion(null)}>
                  <FaArrowLeft size={10} />
                </Button>
              </motion.div>
              <Dialog.Title>{t('indicator.drawer.add.title')}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body overflowY={'scroll'} style={{ maxHeight: '500px' }}>
              {!selectedSuggestion ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: selectedSuggestion ? 0 : 1,
                    left: selectedSuggestion ? '0%' : 'auto',
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <DataList.Root orientation="vertical" divideY="1px">
                    <DataList.Item pt="4" w={'100%'}>
                      <DataList.ItemLabel>{t('indicator.columnsHeader.indicatorName')}</DataList.ItemLabel>
                      <DataList.ItemValue>
                        <form.Field name="name">
                          {(field) => (
                            <Box w={'100%'}>
                              <DebouncedInput
                                id="name"
                                type="text"
                                value={field.state.value}
                                autoFocus
                                onChange={(e) => {
                                  field.handleChange(e as string);
                                  handleSearchTerm(e as string);
                                }}
                              />
                              {!field.state.meta.isValid && (
                                <Text color="red.500" fontSize="sm" mt={2} fontStyle={'italic'}>
                                  {field.state.meta.errors.join(', ')}
                                </Text>
                              )}
                            </Box>
                          )}
                        </form.Field>
                      </DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item pt="4" w={'100%'}>
                      <DataList.ItemLabel>{t('indicator.columnsHeader.unit')}</DataList.ItemLabel>
                      <DataList.ItemValue>
                        <form.Field name="unit">
                          {(field) => (
                            <Box w={'100%'}>
                              <DebouncedInput
                                id="unit"
                                type="text"
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e as string);
                                }}
                              />
                              {!field.state.meta.isValid && (
                                <Text color="red.500" fontSize="sm" mt={2} fontStyle={'italic'}>
                                  {field.state.meta.errors.join(', ')}
                                </Text>
                              )}
                            </Box>
                          )}
                        </form.Field>
                      </DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item pt="4" w={'100%'}>
                      <DataList.ItemLabel>{"Type d'indicateur"}</DataList.ItemLabel>
                      <DataList.ItemValue>
                        <VStack placeItems={'start'} w={'full'}>
                          <form.Field name="category">
                            {(field) => (
                              <IndicatorInput
                                value={field.state.value}
                                onChange={(value) => field.handleChange(value)}
                              />
                            )}
                          </form.Field>
                          {category === 'composite' && (
                            <>
                              <form.Field name="numerator">
                                {(field) => (
                                  <>
                                    <HStack w={'full'} justifyContent={'space-between'}>
                                      <HStack>
                                        <Text fontWeight={'semibold'}>Numerateur</Text>
                                        <FormulaHelp />
                                      </HStack>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        color="primary.600"
                                        _hover={{ color: 'white' }}
                                        onClick={() => field.handleChange('')}
                                      >
                                        {t('ui.clear')}
                                      </Button>
                                    </HStack>

                                    <CompositeIndicatorInput
                                      value={field.state.value}
                                      onChange={(value) => field.handleChange(value)}
                                    />
                                  </>
                                )}
                              </form.Field>
                              <Text fontWeight={'semibold'}>Dénomiateur</Text>
                              <form.Field name="denominator_id">
                                {(field) => (
                                  <SimpleIndicatorInput
                                    value={field.state.value}
                                    onChange={(value) => field.handleChange(value)}
                                  ></SimpleIndicatorInput>
                                )}
                              </form.Field>
                            </>
                          )}
                        </VStack>
                      </DataList.ItemValue>
                    </DataList.Item>
                  </DataList.Root>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: selectedSuggestion ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <DataList.Root orientation="horizontal" divideY="1px">
                    <DataList.Item pt="4" w={'100%'}>
                      <DataList.ItemLabel>{t('indicator.columnsHeader.indicatorName')}</DataList.ItemLabel>
                      <DataList.ItemValue>
                        <Text>{suggestionData?.name}</Text>
                      </DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item pt="4" w={'100%'}>
                      <DataList.ItemLabel>{t('indicator.columnsHeader.categoryName')}</DataList.ItemLabel>
                      <DataList.ItemValue>
                        <Text>{t(`indicator.columnsHeader.category.${suggestionData?.lt_indicator_category}`)}</Text>
                      </DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item pt="6" w={'100%'}>
                      <DataList.ItemLabel>{t('indicator.columnsHeader.description')}</DataList.ItemLabel>
                      <DataList.ItemValue>
                        {suggestionData?.description ? (
                          <Text className="ql-editor ql-viewer" as="div">
                            {parse(DOMPurify.sanitize(suggestionData.description))}
                          </Text>
                        ) : (
                          <Text color="gray.500" textStyle="sm">
                            N/R
                          </Text>
                        )}
                      </DataList.ItemValue>
                    </DataList.Item>
                    {suggestionData?.lt_indicator_category === 'composite' && (
                      <DataList.Item pt="4" w={'100%'}>
                        <DataList.ItemLabel>{t('indicator.columnsHeader.formula')}</DataList.ItemLabel>
                        <DataList.ItemValue>
                          <Text>{suggestionData?.formula}</Text>
                        </DataList.ItemValue>
                      </DataList.Item>
                    )}
                    <DataList.Item pt="4" w={'100%'}>
                      <DataList.ItemLabel>{t('indicator.columnsHeader.unit')}</DataList.ItemLabel>
                      {suggestionData?.lt_indicator_category === 'simple' && (
                        <DataList.ItemValue>
                          <suggestionForm.Field name="unit">
                            {(field) => (
                              <Box w={'100%'}>
                                <SelectUnits
                                  slug={slug}
                                  unitListString={suggestionData?.lt_indicator_other_unit ?? ''}
                                  value={field.state.value !== '' ? [field.state.value] : []}
                                  onChange={(e) => field.handleChange(e.length > 0 ? e[0] : '')}
                                />
                                {!field.state.meta.isValid && (
                                  <Text color="red.500" fontSize="sm" mt={2} fontStyle={'italic'}>
                                    {field.state.meta.errors.join(', ')}
                                  </Text>
                                )}
                              </Box>
                            )}
                          </suggestionForm.Field>
                        </DataList.ItemValue>
                      )}
                      {suggestionData?.lt_indicator_category === 'composite' && (
                        <DataList.ItemValue flexWrap={'wrap'} gap={4}>
                          {suggestionFormUnits.map((unit, index) => (
                            <suggestionForm.Field key={unit.suggestion_id} name={`units[${index}].unit`}>
                              {(field) => {
                                const indicator = suggestionData?.formula_units_per_indicator?.[index];

                                if (!indicator) {
                                  return <Text>N/R</Text>;
                                }
                                return (
                                  <Box w={'100%'}>
                                    <SelectUnits
                                      slug={slug}
                                      label={indicator.suggestion_name}
                                      unitListString={indicator.suggestion_units ?? ''}
                                      value={field.state.value !== '' ? [field.state.value] : []}
                                      onChange={(e) => field.handleChange(e.length > 0 ? e[0] : '')}
                                    />
                                    {!field.state.meta.isValid && (
                                      <Text color="red.500" fontSize="sm" mt={2} fontStyle={'italic'}>
                                        {field.state.meta.errors.join(', ')}
                                      </Text>
                                    )}
                                  </Box>
                                );
                              }}
                            </suggestionForm.Field>
                          ))}
                        </DataList.ItemValue>
                      )}
                    </DataList.Item>
                  </DataList.Root>
                </motion.div>
              )}

              <DataList.Root orientation="horizontal" mt="4">
                <DataList.Item pt="3" w={'100%'} borderTopWidth={1}>
                  <DataList.ItemLabel lineHeight={4}>
                    {t('indicator.columnsHeader.pillarName')}
                    <br />
                    {t('indicator.columnsHeader.commitmentName')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <form.Field name="commitment_id">
                      {(field) => (
                        <SelectCommitment
                          value={field.state.value}
                          onChange={(e) => {
                            field.handleChange(e);
                            suggestionForm.setFieldValue('commitment_id', form.getFieldValue('commitment_id'));
                          }}
                          onLine={false}
                        />
                      )}
                    </form.Field>
                  </DataList.ItemValue>
                </DataList.Item>
              </DataList.Root>
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: suggestionsData && suggestionsData.data.length > 0 && !selectedSuggestion ? 1 : 0,
                  height: suggestionsData && suggestionsData.data.length > 0 && !selectedSuggestion ? 'auto' : 0,
                }}
                transition={{ duration: 0.1, ease: 'easeInOut' }}
              >
                <HStack my={4}>
                  <Separator flex="1" />
                  <Icon color="primary">
                    <FaRegLightbulb />
                  </Icon>
                  <Text flexShrink="0" color="primary" fontSize="md">
                    {t('indicator.drawer.add.suggestions')}
                  </Text>
                  <Separator flex="1" />
                </HStack>
                {suggestionsData?.data.map((sug) => (
                  <HStack key={sug.id} alignItems={'start'} gap="12px">
                    <Link onClick={() => setSelectedSuggestion(sug)} cursor="pointer" color="blue.500">
                      {sug.name} <LuExternalLink />
                    </Link>
                  </HStack>
                ))}
              </motion.div>
            </Dialog.Body>

            <Dialog.Footer>
              <Button onClick={() => resetModalBehavior()} variant="outline">
                {t('ui.cancel')}
              </Button>
              {!selectedSuggestion && (
                <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                  {([canSubmit, isSubmitting]) => (
                    <Button type="submit" disabled={!canSubmit}>
                      {t('ui.save')} {isSubmitting ? '...' : ''}
                    </Button>
                  )}
                </form.Subscribe>
              )}
              {selectedSuggestion && (
                <suggestionForm.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                  {([canSubmit, isSubmitting]) => (
                    <Button type="submit" disabled={!canSubmit}>
                      {t('ui.save')} {isSubmitting ? '...' : ''}
                    </Button>
                  )}
                </suggestionForm.Subscribe>
              )}
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default IndicatorAddDialog;

export const FormulaHelp = () => {
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <IconButton
          aria-label="Aide formules"
          variant="ghost"
          size="sm"
          p={0}
          minW="auto"
          h="auto"
          _hover={{ color: 'white' }}
        >
          <AiOutlineInfoCircle color={'#FF9500'} size={18} />
        </IconButton>
      </Dialog.Trigger>

      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="lg">
            <Dialog.Header>
              <Dialog.Title>{t('indicator.formulaDialog.title')}</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />

            <Dialog.Body>
              <VStack align="stretch">
                <Text border={'1px solid #269584'} padding={3} borderRadius={'3px'}>
                  {t('indicator.formulaDialog.description')}
                </Text>

                <Box bg="green.50" p={3} borderRadius="md">
                  <Text fontWeight="bold" mb={2} as={Flex} columnGap={2} alignItems={'center'}>
                    <PiSealWarningLight color="#F54A00" />
                    {t('indicator.formulaDialog.exempleTitle')}
                  </Text>
                  <Flex flexDirection={'column'} rowGap={3}>
                    <Flex alignItems={'center'} columnGap={2}>
                      <Text bg="white" p={2} borderRadius="md">
                        @Nombre de salariés ayant eu une promotion
                      </Text>
                      <Text textAlign="center">+</Text>
                    </Flex>

                    <Flex alignItems={'center'} columnGap={2}>
                      <Text bg="white" p={2} borderRadius="md">
                        @Nombre d'ETP
                      </Text>
                      <Text textAlign="center">x 100</Text>
                    </Flex>
                  </Flex>
                </Box>

                <Box bg="orange.50" p={3} borderRadius="md" as={Flex} rowGap={2} flexDirection={'column'}>
                  <Text fontWeight="bold" as={Flex} columnGap={2} alignItems={'center'}>
                    <PiSealWarningLight color="#F54A00" /> {t('indicator.formulaDialog.attention')}
                  </Text>
                  <Text fontSize="sm">{t('indicator.formulaDialog.attentionMessage')}</Text>
                </Box>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button w={'full'} bg={'#249484'} color={'white'}>
                  {t('indicator.formulaDialog.buttonCancel')}
                </Button>
              </Dialog.ActionTrigger>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
